import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Minimize2, Maximize2, User, Bot, Ticket } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: '¡Hola! 👋 Soy el asistente virtual de Purolink. ¿En qué puedo ayudarte hoy?'
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    user_name: '',
    user_email: '',
    order_number: '',
    subject: ''
  });
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/api/chat`, {
        message: userMessage,
        session_id: sessionId,
        user_name: ticketForm.user_name || null,
        user_email: ticketForm.user_email || null
      });

      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Lo siento, ha ocurrido un error. ¿Quieres crear un ticket de soporte para que te contactemos?' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const createTicket = async () => {
    if (!ticketForm.user_name || !ticketForm.user_email) {
      toast.error('Por favor, introduce tu nombre y email');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/api/chat/ticket`, {
        session_id: sessionId,
        user_name: ticketForm.user_name,
        user_email: ticketForm.user_email,
        order_number: ticketForm.order_number || null,
        subject: ticketForm.subject || 'Consulta de soporte'
      });

      toast.success(`Ticket #${response.data.ticket_number} creado`);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `✅ ${response.data.message}`
      }]);
      setShowTicketForm(false);
    } catch (error) {
      console.error('Ticket error:', error);
      toast.error('Error al crear el ticket');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 bg-red-600 hover:bg-red-700 text-white rounded-full p-4 shadow-lg transition-all duration-300 hover:scale-110"
        aria-label="Abrir chat"
        data-testid="chat-widget-button"
      >
        <MessageCircle className="h-6 w-6" />
      </button>
    );
  }

  if (isMinimized) {
    return (
      <div 
        className="fixed bottom-6 right-6 z-50 bg-white rounded-lg shadow-lg border cursor-pointer hover:shadow-xl transition-shadow"
        onClick={() => setIsMinimized(false)}
        data-testid="chat-widget-minimized"
      >
        <div className="flex items-center gap-2 px-4 py-3">
          <MessageCircle className="h-5 w-5 text-red-600" />
          <span className="font-medium text-sm">Chat de Soporte</span>
          <Maximize2 className="h-4 w-4 text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <Card 
      className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] shadow-2xl border-0"
      data-testid="chat-widget"
    >
      {/* Header */}
      <CardHeader className="bg-red-600 text-white rounded-t-lg py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            <CardTitle className="text-base font-medium">Chat de Soporte</CardTitle>
          </div>
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-white hover:bg-red-700"
              onClick={() => setIsMinimized(true)}
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-white hover:bg-red-700"
              onClick={() => setIsOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {/* Messages */}
        <ScrollArea className="h-[350px] p-4">
          <div className="space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'assistant' && (
                  <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <Bot className="h-4 w-4 text-red-600" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                    msg.role === 'user'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {msg.content}
                </div>
                {msg.role === 'user' && (
                  <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <User className="h-4 w-4 text-gray-600" />
                  </div>
                )}
              </div>
            ))}
            {isLoading && (
              <div className="flex gap-2 justify-start">
                <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-red-600" />
                </div>
                <div className="bg-gray-100 rounded-lg px-3 py-2">
                  <div className="flex gap-1">
                    <span className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Ticket Form */}
        {showTicketForm && (
          <div className="border-t p-4 bg-yellow-50">
            <p className="text-sm font-medium text-yellow-800 mb-3">
              🎫 Crear Ticket de Soporte
            </p>
            <div className="space-y-2">
              <Input
                placeholder="Tu nombre *"
                value={ticketForm.user_name}
                onChange={(e) => setTicketForm({ ...ticketForm, user_name: e.target.value })}
                className="h-9 text-sm"
              />
              <Input
                type="email"
                placeholder="Tu email *"
                value={ticketForm.user_email}
                onChange={(e) => setTicketForm({ ...ticketForm, user_email: e.target.value })}
                className="h-9 text-sm"
              />
              <Input
                placeholder="Nº de pedido (opcional)"
                value={ticketForm.order_number}
                onChange={(e) => setTicketForm({ ...ticketForm, order_number: e.target.value })}
                className="h-9 text-sm"
              />
              <div className="flex gap-2">
                <Button
                  onClick={createTicket}
                  disabled={isLoading}
                  className="flex-1 h-9 bg-yellow-600 hover:bg-yellow-700"
                >
                  Crear Ticket
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowTicketForm(false)}
                  className="h-9"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Input */}
        <div className="border-t p-3">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10 flex-shrink-0"
              onClick={() => setShowTicketForm(!showTicketForm)}
              title="Crear ticket de soporte"
            >
              <Ticket className="h-4 w-4" />
            </Button>
            <Input
              ref={inputRef}
              placeholder="Escribe tu mensaje..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              className="h-10"
              data-testid="chat-input"
            />
            <Button
              onClick={sendMessage}
              disabled={isLoading || !inputMessage.trim()}
              className="h-10 w-10 bg-red-600 hover:bg-red-700 p-0"
              data-testid="chat-send"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChatWidget;
